import { observable, action } from "mobx";
import BaseStore from "common/BaseStore";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSService } from "modules/WBS/WBSService";
import {
  ITableOccurencesRow,
  IWBSItemData,
  IWBSItemOcurrence,
} from "common/interfaces";
import { divide } from "utils/DecimalUtils";
import { sortString } from "utils/StringUtils";

class Store extends BaseStore {
  /** Define se a tela de ocorrências deve ser aberta ou não. */
  @observable
  public showOccurencesLayer: boolean = false;
  /** Define se o loading será exibido. */
  @observable
  public loading: boolean = false;
  /** Define se o sistema deve redirecionar a página atual para
   * a página principal da EAP.
   */
  @observable
  public redirectToEAP: boolean = false;
  /** Dados do item selecionado. */
  private _currentItem?: IWBSItemData;
  /** Lista com todas as ocorrências do item na EAP. */
  private _occurences: IWBSItemOcurrence[] = [];

  constructor() {
    super("WBSItemOccurrencesStore");
  }

  @action
  reset() {
    this.showOccurencesLayer = false;
    this.loading = false;
    this.redirectToEAP = false;
    this._currentItem = undefined;
    this._occurences = [];
  }

  /**
   * Retorna os dados do item selecionado tratando possíveis
   * valores vazios.
   */
  getItemData() {
    const item = this._currentItem;

    return {
      code: item ? item.code : "-",
      description: item ? item.description : "",
      unit: item ? item.unit : "-",
      unitCost: item ? item.unitCost : 0,
      equipment: item ? item.equipment : 0,
      labor: item ? item.labor : 0,
      material: item ? item.material : 0,
      others: item ? item.others : 0,
    };
  }

  /** Retorna uma lista de linhas que serão exibidas em um componente
   * tabela usando os dados da lista de ocorrências atual.
   * @returns {ITableOccurencesRow[]} Lista de linhas da tabela
   */
  getRows(): ITableOccurencesRow[] {
    const rows: ITableOccurencesRow[] = [];
    const item = this._currentItem;

    if (!item) return rows;

    // Obtém os códigos da EAP
    for (const occurence of this._occurences) {
      const rowOfEAP = WBSStore.findEAPRowById(occurence.idForTree);
      const treeCode =
        rowOfEAP && rowOfEAP.data ? (rowOfEAP.data as any).tree : "";

      occurence.treeCode = treeCode;
    }

    // Ordena lista
    const orderedOccurences = this._occurences.sort((a, b) =>
      sortString(a.treeCode || "", b.treeCode || "")
    );

    // Monta lista de linhas a ser exibida na tabela de ocorrências
    for (const occurence of orderedOccurences) {
      const treeRowId = occurence.idForTree;
      const treeCode = occurence.treeCode || "";
      let indent = 1;

      if (occurence.parentTasks.length) {
        occurence.parentTasks.forEach((occ) => {
          rows.push({
            id: occ.id,
            treeCode: treeRowId === occ.id ? treeCode : "",
            description: occ.description,
            quantity: occ.quantityAccumulated,
            unit: occ.unit,
            unitCost: occ.unitCost,
            total: occ.totalAccumulated,
            percentual: 0,
            isParent: true,
            indent,
          });
          indent++;
        });
      }

      rows.push({
        id: occurence.id,
        treeCode: treeRowId === occurence.id ? treeCode : "",
        description: item.description,
        quantity: occurence.quantityAccumulated,
        unit: item.unit,
        unitCost: item.unitCost,
        total: occurence.totalAccumulated,
        percentual:
          divide(occurence.quantityAccumulated, item.quantityTotal) * 100,
        indent,
      });
    }
    return rows;
  }

  /** Altera a propriedade redirectToEAP para permitir o redirecionamento para página
   * principal da EAP, somente se uma composição estiver aberta.
   *
   * @returns {boolean} Retorna se a página vai ser redirecionada.
   */
  @action
  enableRedirectIfTaskIsOpen(): boolean {
    if (WBSStore.task) {
      this.redirectToEAP = true;
      return true;
    }
    return false;
  }

  /** Abre a janela modal de ocorrências do item. */
  @action
  openOccurences() {
    this.showOccurencesLayer = true;
    this.redirectToEAP = false;
  }

  /** Fecha a janela modal de ocorrências do item. */
  @action
  closeOccurences() {
    this.showOccurencesLayer = false;
    this.loading = false;
    this.redirectToEAP = false;
  }

  /** Altera as propriedades de _currentItem e _occurences. */
  @action
  setItemOccurences(newItem: IWBSItemData, newOccurences: IWBSItemOcurrence[]) {
    this._currentItem = newItem;
    this._occurences = newOccurences;
  }

  /** Altera a propriedade de loading habilitando/desabilitando
   * o layer de carregando da tela de ocorrências. */
  @action
  setLoading(value: boolean) {
    this.loading = value;
  }

  /**
   * Obtém os dados das ocorrências de um insumo/composição na EAP.
   * @param {string} contentId ID do conteúdo da linha selecionada
   * @returns {Promise<void>}
   */
  async fetchAndShowItemOccurences(contentId: string) {
    const wbsId = WBSStore.WBS.id;
    if (!wbsId || !contentId) return;

    this.reset();
    this.openOccurences();
    this.setLoading(true);

    WBSService.getOccurrencesOfItem(wbsId, contentId).then((newOccurences) => {
      this.setItemOccurences(newOccurences.item, newOccurences.rows);
      this.setLoading(false);
    });
  }
}

export const WBSItemOccurrencesStore = new Store();



// WEBPACK FOOTER //
// ./src/modules/WBS/WBSItemOccurrencesStore.ts